import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Intensity } from '../utils/api';
import { StackedBar, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { INTENSITY_COLORS, fmtNum, fmtPct, fmtMonth } from '../utils/theme';

// Heavy → Medium → Light (H/M/L order).
const LEVELS = ['Heavy', 'Medium', 'Light'];
// Light → Heavy for the stacked-mix trend so Heavy sits on top.
const MIX_ORDER = ['Light', 'Medium', 'Heavy'];

// Last 6 months ending at the selected dashboard month (ISO strings sort lexically).
function window6(allMonths, endMonth) {
  return allMonths.filter(m => !endMonth || m <= endMonth).slice(-6);
}

function buildIntensityShares(rows, months, valueKey) {
  return MIX_ORDER.filter(lvl => rows.some(r => r.intensity === lvl)).map(lvl => {
    const byMonth = Object.fromEntries(rows.filter(r => r.intensity === lvl).map(r => [r.month_id, r[valueKey]]));
    return { name: lvl, color: INTENSITY_COLORS[lvl], data: months.map(m => byMonth[m] ?? 0) };
  });
}

export default function CategoryHmlProfileView() {
  const { category }  = useCategory();
  const meta          = useMetaData();
  const { month }     = useMonth();
  const [sessionType, setSessionType] = useState('open_app');

  const selMonth = month || meta.latestMonth;

  const { data, loading, error } = useApi(
    () => Intensity.snapshot({ category, session_type: sessionType, month_id: selMonth }),
    { category, sessionType, selMonth },
    !!category && !!selMonth,
  );

  // 6-month intensity-mix trend (category totals per intensity per month).
  const trend = useApi(
    () => Intensity.trend({ category, session_type: sessionType }),
    { category, sessionType },
    !!category,
  );
  const mixMonths = useMemo(
    () => window6([...new Set((trend.data || []).map(r => r.month_id))].sort(), selMonth),
    [trend.data, selMonth],
  );
  const userShares    = useMemo(() => (trend.data ? buildIntensityShares(trend.data, mixMonths, 'active_users')        : []), [trend.data, mixMonths]);
  const sessionShares = useMemo(() => (trend.data ? buildIntensityShares(trend.data, mixMonths, 'total_15min_sessions') : []), [trend.data, mixMonths]);

  // Category totals per intensity — 'Any App' rows are the deduplicated totals.
  const dist = useMemo(() => {
    if (!data?.length) return [];
    const anyApp = data.filter(r => r.application === 'Any App');
    return LEVELS.map(lvl => {
      const rows = anyApp.filter(r => r.intensity === lvl);
      const users    = rows.reduce((s, r) => s + (r.active_users || 0), 0);
      const sessions = rows.reduce((s, r) => s + (r.total_15min_sessions || 0), 0);
      return { level: lvl, color: INTENSITY_COLORS[lvl], users, sessions };
    }).filter(d => d.users > 0);
  }, [data]);

  const totalUsers    = dist.reduce((s, d) => s + d.users, 0);
  const totalSessions = dist.reduce((s, d) => s + d.sessions, 0);

  // Two 100%-stacked bars: user share and session share across H/M/L.
  const shareSeries = dist.map(d => ({
    name: d.level, color: d.color, data: [d.users, d.sessions],
  }));

  if (meta.loading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <ChartCard
        title="Category Split by Intensity — Heavy / Medium / Light"
        sub={`${category} · ${fmtMonth(selMonth)} · ${sessionType} · share of users vs sessions`}
        style={{ marginBottom: 16 }}
      >
        {loading  && <ChartLoader height={220} />}
        {error    && <ChartError message={error} height={220} />}
        {!loading && !error && dist.length > 0 && (
          <StackedBar categories={['Users', 'Sessions']} series={shareSeries} height={220} horizontal normalize />
        )}
        {!loading && !error && dist.length === 0 && (
          <div className="empty-state">No intensity data for this category.</div>
        )}
      </ChartCard>

      {!loading && !error && dist.length > 0 && (
        <ChartCard title="Breakdown" sub={`${category} · ${fmtMonth(selMonth)}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Intensity</th>
                <th style={{ textAlign: 'right' }}>Active Users</th>
                <th style={{ textAlign: 'right' }}>% of Users</th>
                <th style={{ textAlign: 'right' }}>Sessions</th>
                <th style={{ textAlign: 'right' }}>% of Sessions</th>
                <th style={{ textAlign: 'right' }}>Avg Sessions / User</th>
              </tr>
            </thead>
            <tbody>
              {dist.map(d => (
                <tr key={d.level}>
                  <td>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                      background: d.color, marginRight: 8,
                    }} />
                    {d.level}
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(d.users)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtPct(totalUsers ? (d.users / totalUsers) * 100 : 0)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(d.sessions)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtPct(totalSessions ? (d.sessions / totalSessions) * 100 : 0)}</td>
                  <td style={{ textAlign: 'right' }}>{d.users ? (d.sessions / d.users).toFixed(1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
      )}

      {/* ── 6-month intensity-mix trend ─────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <ChartCard title="Intensity Mix — Users" sub={`${category} · ${sessionType} · % of users by H/M/L`}>
          {trend.loading && <ChartLoader height={300} />}
          {trend.error   && <ChartError message={trend.error} height={300} />}
          {!trend.loading && !trend.error && userShares.length > 0 && (
            <StackedBar categories={mixMonths.map(fmtMonth)} series={userShares} normalize height={300} />
          )}
          {!trend.loading && !trend.error && userShares.length === 0 && <div className="empty-state">No data</div>}
        </ChartCard>

        <ChartCard title="Intensity Mix — Sessions" sub={`${category} · ${sessionType} · % of sessions by H/M/L`}>
          {trend.loading && <ChartLoader height={300} />}
          {trend.error   && <ChartError message={trend.error} height={300} />}
          {!trend.loading && !trend.error && sessionShares.length > 0 && (
            <StackedBar categories={mixMonths.map(fmtMonth)} series={sessionShares} normalize height={300} />
          )}
          {!trend.loading && !trend.error && sessionShares.length === 0 && <div className="empty-state">No data</div>}
        </ChartCard>
      </div>
    </div>
  );
}
